// ═══════════════════════════════════════════════════════════════════════
//  FinancialMind.ai — Country Configuration (Multi-Jurisdiction)
//  Configuração regulatória, fiscal e de formatação por país
//  Suporte: BR (Brasil), US (EUA), MX (México), CN (China)
// ═══════════════════════════════════════════════════════════════════════

export const COUNTRY_CONFIGS = {

  // ─── BRASIL ────────────────────────────────────────────────────────
  BR: {
    name: "Brasil",
    flag: "🇧🇷",
    currency: { code: "BRL", symbol: "R$", locale: "pt-BR", decimals: 2 },
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ",", thousands: "." },

    // Identificadores corporativos e pessoais
    entity: {
      companyId:   { key: "cnpj",    label: "CNPJ",    mask: "00.000.000/0000-00", digits: 14, validator: "validateCNPJ" },
      personalId:  { key: "cpf",     label: "CPF",     mask: "000.000.000-00",      digits: 11, validator: "validateCPF" },
      stateRegId:  { key: "ie",      label: "Inscrição Estadual", mask: null, digits: null },
      tradeName:   { key: "nome_fantasia",  label: "Nome Fantasia" },
      legalName:   { key: "razao_social",   label: "Razão Social" },
    },

    // Regulamentações
    compliance: {
      dataProtection:  { law: "LGPD", fullName: "Lei Geral de Proteção de Dados", ref: "Lei 13.709/2018" },
      electronicInvoice: { system: "NF-e", authority: "Receita Federal / SEFAZ" },
      centralBank:     { name: "BCB", fullName: "Banco Central do Brasil", rateKey: "SELIC" },
      taxAuthority:    { name: "Receita Federal do Brasil", acronym: "RFB" },
      corporateLaw:    { type: "Ltda / S.A.", registrar: "Junta Comercial" },
    },

    // Faturamento / Invoicing
    invoicing: {
      type: "NF-e",                  // Nota Fiscal Eletrônica (obrigatória)
      authority: "Prefeitura / SEFAZ",
      municipalSystem: true,         // Vinculado ao sistema da prefeitura (ex: SP)
      taxOnInvoice: true,            // ISS, PIS, COFINS, CSLL aparecem na NF
      currency: "BRL",
      requiredFields: ["cnpj_emitente", "cnpj_tomador", "codigo_servico", "iss_retido", "aliquota_iss"],
      electronicRequired: true,      // NF-e obrigatória por lei
      format: "XML (padrão ABRASF)",
    },

    // Dados de mercado
    marketData: {
      riskFreeRate:    { source: "BCB", series: "432", name: "SELIC Meta", url: "https://api.bcb.gov.br/dados/serie/bcdata.sgs.432/dados/ultimos/1?formato=json" },
      inflationIndex:  { name: "IPCA", source: "IBGE" },
      creditBureau:    [ "Serasa Experian", "SPC Brasil", "Boa Vista SCPC" ],
    },

    // Campos de formulário de registro
    registrationFields: ["name", "email", "cpf", "cargo", "company", "cnpj", "razao_social", "password"],

    // Estrutura societária
    ownershipFields: {
      shareholders: { idField: "cpf", idLabel: "CPF", nameField: "nome", shareField: "participacao" },
    },

    // Demonstrações financeiras
    financialStatements: {
      standard: "BR-GAAP / CPC",
      periods: { fiscal_year_end: "12/31", interim: "quarterly" },
      required: ["balanco_patrimonial", "dre", "dfc", "dmpl"],
    },
  },

  // ─── ESTADOS UNIDOS ────────────────────────────────────────────────
  US: {
    name: "United States",
    flag: "🇺🇸",
    currency: { code: "USD", symbol: "$", locale: "en-US", decimals: 2 },
    dateFormat: "MM/dd/yyyy",
    numberFormat: { decimal: ".", thousands: "," },

    entity: {
      companyId:   { key: "ein",     label: "EIN",     mask: "00-0000000",          digits: 9,  validator: "validateEIN" },
      personalId:  { key: "ssn",     label: "SSN",     mask: "000-00-0000",         digits: 9,  validator: "validateSSN", sensitive: true },
      stateRegId:  { key: "state_id", label: "State Tax ID", mask: null, digits: null },
      tradeName:   { key: "dba",            label: "DBA (Doing Business As)" },
      legalName:   { key: "legal_name",     label: "Legal Name" },
    },

    compliance: {
      dataProtection:  { law: "CCPA/CPRA", fullName: "California Consumer Privacy Act / California Privacy Rights Act", ref: "Cal. Civ. Code § 1798.100" },
      electronicInvoice: { system: "Invoice (standard)", authority: "IRS" },
      centralBank:     { name: "Fed", fullName: "Federal Reserve System", rateKey: "Fed Funds Rate" },
      taxAuthority:    { name: "Internal Revenue Service", acronym: "IRS" },
      corporateLaw:    { type: "LLC / C-Corp / S-Corp", registrar: "Secretary of State" },
      additionalCompliance: [
        { law: "SOX", fullName: "Sarbanes-Oxley Act", applies: "public companies" },
        { law: "Dodd-Frank", fullName: "Dodd-Frank Wall Street Reform Act", applies: "financial institutions" },
        { law: "GLBA", fullName: "Gramm-Leach-Bliley Act", applies: "financial institutions" },
      ],
    },

    // Invoicing — US uses Stripe Invoicing + Stripe Tax
    invoicing: {
      type: "Stripe Invoice",           // Stripe-generated PDF invoice
      system: "stripe",                 // Backend identifier for invoicing system
      authority: "IRS (tax reporting only)",
      municipalSystem: false,           // No municipal e-invoice system required
      taxOnInvoice: true,               // Stripe Tax adds state sales tax automatically
      currency: "USD",
      requiredFields: ["company_name", "ein", "billing_address", "invoice_number", "payment_terms"],
      electronicRequired: false,        // Electronic invoice is voluntary in US
      format: "PDF (Stripe-generated)",

      // Stripe Tax configuration
      stripeTax: {
        enabled: true,                  // automatic_tax: { enabled: true }
        type: "state-level",            // Each state has its own rules
        productTaxCode: "txcd_10103001", // SaaS - Software as a Service
        collectible: "varies by nexus", // Depends on economic nexus
        note: "Stripe Tax automatically calculates state sales tax based on customer billing address",
        taxableStates: "~25 states",    // Approximately 25 states tax SaaS
        exemptStates: "~25 states",     // Many states exempt SaaS B2B
      },

      // Stripe Invoicing configuration
      stripeInvoicing: {
        enabled: true,                  // invoice_creation: { enabled: true }
        autoGenerate: true,             // Stripe auto-generates invoice on subscription payment
        pdfHosted: true,                // Stripe hosts downloadable PDF
        customFields: [
          { name: "Company EIN", value: "from_customer" },
          { name: "Payment Terms", value: "Net 30" },
        ],
        footer: "FinancialMind.ai | Keymind Technology Inc. | Payment due within 30 days.",
      },

      paymentTerms: "Net 30",           // US standard
      acceptedMethods: ["ACH", "Wire", "Credit Card"],
      paymentProcessor: "Stripe",
    },

    marketData: {
      riskFreeRate:    { source: "FRED", series: "FEDFUNDS", name: "Fed Funds Rate", url: "https://api.stlouisfed.org/fred/series/observations?series_id=FEDFUNDS&sort_order=desc&limit=1&api_key=DEMO&file_type=json" },
      inflationIndex:  { name: "CPI", source: "BLS" },
      creditBureau:    [ "Experian", "Equifax", "TransUnion" ],
      businessCredit:  [ "Dun & Bradstreet (DUNS)", "Experian Business", "Equifax Business" ],
    },

    registrationFields: ["name", "email", "ssn_last4", "title", "company", "ein", "legal_name", "password"],

    ownershipFields: {
      shareholders: { idField: "ssn_last4", idLabel: "SSN (last 4)", nameField: "name", shareField: "ownership_pct" },
    },

    financialStatements: {
      standard: "US-GAAP",
      periods: { fiscal_year_end: "variable", interim: "quarterly" },
      required: ["balance_sheet", "income_statement", "cash_flow_statement", "stockholders_equity"],
    },
  },

  // ─── MÉXICO ────────────────────────────────────────────────────────
  MX: {
    name: "México",
    flag: "🇲🇽",
    currency: { code: "MXN", symbol: "MX$", locale: "es-MX", decimals: 2 },
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ".", thousands: "," },

    entity: {
      companyId:   { key: "rfc",     label: "RFC",     mask: "AAAA000000XX0", digits: 13, validator: "validateRFC" },
      personalId:  { key: "curp",    label: "CURP",    mask: "AAAA000000AAAAAA00", digits: 18, validator: "validateCURP" },
      stateRegId:  { key: "cedula_fiscal", label: "Cédula Fiscal", mask: null, digits: null },
      tradeName:   { key: "nombre_comercial", label: "Nombre Comercial" },
      legalName:   { key: "razon_social",     label: "Razón Social" },
    },

    compliance: {
      dataProtection:  { law: "LFPDPPP", fullName: "Ley Federal de Protección de Datos Personales en Posesión de los Particulares" },
      electronicInvoice: { system: "CFDI", authority: "SAT" },
      centralBank:     { name: "Banxico", fullName: "Banco de México", rateKey: "Tasa Objetivo" },
      taxAuthority:    { name: "Servicio de Administración Tributaria", acronym: "SAT" },
      corporateLaw:    { type: "S.A. de C.V. / S. de R.L.", registrar: "Registro Público de Comercio" },
    },

    marketData: {
      riskFreeRate:    { source: "Banxico", series: "SF43783", name: "Tasa Objetivo", url: "https://www.banxico.org.mx/SieAPIRest/service/v1/series/SF43783/datos/oportuno?token=DEMO" },
      inflationIndex:  { name: "INPC", source: "INEGI" },
      creditBureau:    [ "Buró de Crédito", "Círculo de Crédito" ],
    },

    registrationFields: ["name", "email", "curp", "cargo", "company", "rfc", "razon_social", "password"],

    ownershipFields: {
      shareholders: { idField: "curp", idLabel: "CURP", nameField: "nombre", shareField: "participacion" },
    },

    financialStatements: {
      standard: "NIF (Normas de Información Financiera)",
      periods: { fiscal_year_end: "12/31", interim: "quarterly" },
      required: ["balance_general", "estado_resultados", "flujo_efectivo"],
    },
  },

  // ─── CHINA ─────────────────────────────────────────────────────────
  CN: {
    name: "中国 (China)",
    flag: "🇨🇳",
    currency: { code: "CNY", symbol: "¥", locale: "zh-CN", decimals: 2 },
    dateFormat: "yyyy-MM-dd",
    numberFormat: { decimal: ".", thousands: "," },

    entity: {
      companyId:   { key: "uscc",    label: "USCC (统一社会信用代码)", mask: null, digits: 18, validator: "validateUSCC" },
      personalId:  { key: "id_card", label: "身份证号码 (ID Card)", mask: null, digits: 18, validator: "validateChinaID" },
      stateRegId:  { key: "tax_reg", label: "税务登记号", mask: null, digits: null },
      tradeName:   { key: "trade_name_cn", label: "商号" },
      legalName:   { key: "company_name_cn", label: "企业名称" },
    },

    compliance: {
      dataProtection:  { law: "PIPL", fullName: "个人信息保护法 (Personal Information Protection Law)" },
      electronicInvoice: { system: "Fapiao (发票)", authority: "STA" },
      centralBank:     { name: "PBOC", fullName: "中国人民银行 (People's Bank of China)", rateKey: "LPR" },
      taxAuthority:    { name: "国家税务总局 (State Taxation Administration)", acronym: "STA" },
      corporateLaw:    { type: "有限责任公司 / 股份有限公司", registrar: "SAMR (市场监督管理总局)" },
    },

    marketData: {
      riskFreeRate:    { source: "PBOC", series: "LPR_1Y", name: "LPR 1-Year", url: null },
      inflationIndex:  { name: "CPI", source: "NBS" },
      creditBureau:    [ "PBOC Credit Reference Center (征信中心)", "Baihang Credit (百行征信)" ],
    },

    registrationFields: ["name", "email", "id_card_last4", "title", "company", "uscc", "company_name_cn", "password"],

    ownershipFields: {
      shareholders: { idField: "id_card_last4", idLabel: "身份证 (last 4)", nameField: "name", shareField: "ownership_pct" },
    },

    financialStatements: {
      standard: "CAS (中国会计准则)",
      periods: { fiscal_year_end: "12/31", interim: "quarterly" },
      required: ["balance_sheet", "income_statement", "cash_flow_statement"],
    },
  },
};

// ─── Helper: detectar país pela configuração do tenant ───────────────
export function getCountryConfig(countryCode = "BR") {
  return COUNTRY_CONFIGS[countryCode.toUpperCase()] || COUNTRY_CONFIGS.BR;
}

// ─── Formatadores dinâmicos por país ─────────────────────────────────
export function formatCurrency(value, countryCode = "BR") {
  const cfg = getCountryConfig(countryCode);
  if (value == null || value === "" || isNaN(+value)) return "—";
  return new Intl.NumberFormat(cfg.currency.locale, {
    style: "currency",
    currency: cfg.currency.code,
    minimumFractionDigits: cfg.currency.decimals,
    maximumFractionDigits: cfg.currency.decimals,
  }).format(+value);
}

export function formatDate(date, countryCode = "BR") {
  const cfg = getCountryConfig(countryCode);
  if (!date) return "—";
  return new Intl.DateTimeFormat(cfg.currency.locale, {
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date(date));
}

export function formatNumber(value, countryCode = "BR", decimals = 2) {
  const cfg = getCountryConfig(countryCode);
  if (value == null || isNaN(+value)) return "—";
  return new Intl.NumberFormat(cfg.currency.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(+value);
}

// ─── Máscaras dinâmicas de ID ────────────────────────────────────────
export function applyIdMask(value, idType, countryCode = "BR") {
  const cfg = getCountryConfig(countryCode);
  const idConfig = cfg.entity[idType];
  if (!idConfig) return value;

  const digits = value.replace(/\D/g, "").slice(0, idConfig.digits);

  // Máscaras específicas por tipo e país
  const masks = {
    BR: {
      cnpj: d => d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5"),
      cpf:  d => d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"),
    },
    US: {
      ein: d => d.replace(/(\d{2})(\d{7})/, "$1-$2"),
      ssn: d => d.replace(/(\d{3})(\d{2})(\d{4})/, "$1-$2-$3"),
    },
    MX: {
      rfc:  d => d.toUpperCase(), // RFC is alphanumeric, no standard mask
      curp: d => d.toUpperCase(),
    },
    CN: {
      uscc:    d => d.toUpperCase(),
      id_card: d => d,
    },
  };

  const countryMasks = masks[countryCode];
  const maskFn = countryMasks?.[idConfig.key];
  return maskFn ? maskFn(digits) : digits;
}

// ─── Validadores de ID por país ──────────────────────────────────────
export const validators = {
  validateCNPJ(cnpj) {
    const d = cnpj.replace(/\D/g, "");
    if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false;
    const calc = (digits, weights) => {
      const sum = weights.reduce((s, w, i) => s + parseInt(digits[i]) * w, 0);
      const r = sum % 11;
      return r < 2 ? 0 : 11 - r;
    };
    const w1 = [5,4,3,2,9,8,7,6,5,4,3,2];
    const w2 = [6,5,4,3,2,9,8,7,6,5,4,3,2];
    return calc(d, w1) === +d[12] && calc(d, w2) === +d[13];
  },

  validateCPF(cpf) {
    const d = cpf.replace(/\D/g, "");
    if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
    for (let t = 9; t < 11; t++) {
      let sum = 0;
      for (let i = 0; i < t; i++) sum += parseInt(d[i]) * (t + 1 - i);
      const r = (sum * 10) % 11;
      if ((r === 10 ? 0 : r) !== parseInt(d[t])) return false;
    }
    return true;
  },

  validateEIN(ein) {
    const d = ein.replace(/\D/g, "");
    if (d.length !== 9) return false;
    // Valid EIN prefixes (IRS campus codes)
    const validPrefixes = [
      "01","02","03","04","05","06","10","11","12","13","14","15","16","20","21","22","23","24","25",
      "26","27","30","32","33","34","35","36","37","38","39","40","41","42","43","44","45","46","47",
      "48","50","51","52","53","54","55","56","57","58","59","60","61","62","63","64","65","66","67",
      "68","71","72","73","74","75","76","77","78","80","81","82","83","84","85","86","87","88","90",
      "91","92","93","94","95","98","99"
    ];
    return validPrefixes.includes(d.slice(0, 2));
  },

  validateSSN(ssn) {
    const d = ssn.replace(/\D/g, "");
    if (d.length !== 9) return false;
    // Cannot start with 000, 666, or 9xx
    const area = parseInt(d.slice(0, 3));
    if (area === 0 || area === 666 || area >= 900) return false;
    // Group and serial cannot be 0000
    if (d.slice(3, 5) === "00" || d.slice(5) === "0000") return false;
    return true;
  },

  validateRFC(rfc) {
    const clean = rfc.toUpperCase().replace(/[\s-]/g, "");
    // Persona moral: 12 chars, Persona física: 13 chars
    return /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(clean);
  },

  validateCURP(curp) {
    const clean = curp.toUpperCase().replace(/[\s-]/g, "");
    return /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/.test(clean);
  },

  validateUSCC(uscc) {
    // Unified Social Credit Code: 18 chars alphanumeric
    const clean = uscc.toUpperCase().replace(/[\s-]/g, "");
    return /^[0-9A-Z]{18}$/.test(clean);
  },

  validateChinaID(id) {
    const d = id.replace(/[\s-]/g, "");
    return /^\d{17}[\dXx]$/.test(d);
  },
};

// ─── Tabela de equivalência rápida (para referência) ─────────────────
export const EQUIVALENCE_TABLE = {
  // Conceito             BR                  US                    MX                  CN
  company_id:           { BR: "CNPJ",         US: "EIN",            MX: "RFC",          CN: "USCC" },
  personal_id:          { BR: "CPF",          US: "SSN",            MX: "CURP",         CN: "身份证" },
  data_protection:      { BR: "LGPD",         US: "CCPA/CPRA",      MX: "LFPDPPP",      CN: "PIPL" },
  tax_authority:        { BR: "Receita (RFB)",US: "IRS",            MX: "SAT",          CN: "STA" },
  central_bank:         { BR: "BCB",          US: "Fed (FRS)",      MX: "Banxico",      CN: "PBOC" },
  benchmark_rate:       { BR: "SELIC",        US: "Fed Funds Rate", MX: "Tasa Objetivo", CN: "LPR" },
  inflation_index:      { BR: "IPCA",         US: "CPI",            MX: "INPC",         CN: "CPI" },
  electronic_invoice:   { BR: "NF-e",         US: "E-Invoice",      MX: "CFDI",         CN: "Fapiao" },
  accounting_standard:  { BR: "CPC/BR-GAAP",  US: "US-GAAP",        MX: "NIF",          CN: "CAS" },
  corp_type:            { BR: "Ltda/S.A.",    US: "LLC/C-Corp",     MX: "S.A. de C.V.", CN: "有限/股份" },
  credit_bureau:        { BR: "Serasa/SPC",   US: "Experian/Equifax/TransUnion", MX: "Buró de Crédito", CN: "征信中心" },
  state_registration:   { BR: "Inscr. Estadual", US: "State Tax ID", MX: "Cédula Fiscal", CN: "税务登记号" },
  trade_name:           { BR: "Nome Fantasia", US: "DBA",           MX: "Nombre Comercial", CN: "商号" },
  legal_name:           { BR: "Razão Social", US: "Legal Name",     MX: "Razón Social",  CN: "企业名称" },
  contract_law:         { BR: "LGPD + CC",    US: "CCPA + UCC",     MX: "LFPDPPP + CC",  CN: "PIPL + CL" },
};

export default COUNTRY_CONFIGS;
